<template>
    <div class="routes-wrap" :class="{ 'mobile-routes': mobile }">
        <router-link
            v-show="level === 1"
            class="navbar-brand py-0"
            :class="{'pl-3': !mobile, 'mb-4': mobile}"
            :to="{name: 'dashboard'}">
            <img :src="isWebExpensesApp ? '/logo-webexpenses.png': '/header.logo.png'"
                 :height="isWebExpensesApp ? 46 : 22"
                 :style="isWebExpensesApp ? '' : 'transform: translateY(-2px)'"
                 width="auto"
                 :alt="`${appDisplayName} logo`">
        </router-link>

        <router-link v-for="section in systemDocumentRouteSections"
                     :key="section.key"
                     :to="{ name: section.route }"
                     :data-link="section.route">
            {{ section.appHeaderTitle }}
        </router-link>

        <template v-if="customVisibleDocTypes.length !== 0">
            <a v-for="item in customVisibleDocTypes" :key="item.key"
               @click.prevent="setCustomDocRoute(item, true)"
               href="#"
               :data-link="item.name"
               :style="{ 'font-weight': checkIsActiveDocTypeLink(item.uuid) ? 600 : 400 }">
                {{ item.name }}
            </a>
        </template>

        <!-- "..." (More) -->
        <template v-if="level === 1 && !mobile">
            <div data-toggle="dropdown" id="more-links"
                 :style="{ visibility: isAllRoutesVisible ? 'hidden' : 'visible' }">
                <i class="fas fa-ellipsis-h"></i>
            </div>
            <div class="dropdown-menu dropdown-menu-right px-2">
                <HeaderRoutes :level="2" id="desktop-routes-more"/>
            </div>
        </template>
    </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import {DOCS_TYPES_SELECTED_SET} from '@/store/modules/documents'
import {docSystemTypesDefaultModels, docSystemTypesDefaultModelsList} from '@/types/document/types'
import {useResizeObserver} from '@vueuse/core'

export default {
    name: 'HeaderRoutes',
    props: {
        level: {
            type: Number,
            default: 1 // level 2 is used for routes collapsed into more dropdown (it heps to keep all listeners and dynamic renders)
        },
        mobile: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            docSystemTypesDefaultModels,
            isAllRoutesVisible: false,
            isResizeProcessing: false
        }
    },
    computed: {
        ...mapGetters('documents', [
            'documentTypeSelected',
            'documentTypes'
        ]),

        ...mapGetters('doc_type_permissions', [
            'checkIsVisibleDocTypeForCurrentUser'
        ]),

        dt() {
            return this.documentTypeSelected
        },

        systemDocumentRouteSections() {
            return docSystemTypesDefaultModelsList
                .filter(t => t.appHeaderTitle && this.checkIsVisibleSystemDocType(t.key))
                .map(t => ({
                    ...t,
                    appHeaderTitle: _.get(this.getDocTypeModel(t.key), 'name', t.appHeaderTitle),
                    appHeaderOrder: _.get(this.getDocTypeModel(t.key), 'order', t.appHeaderOrder)
                }))
                .sort(t => t.appHeaderOrder)
        },

        customVisibleDocTypes() {
            return this.documentTypes
                .filter(dt => !dt.isSystem && dt.isTopLevel && !dt.isHidden && this.checkIsVisibleDocTypeForCurrentUser(dt))
                .sort(dt => dt.order)
        }
    },
    methods: {
        ...mapActions('documents', {
            setDocTypeSelected: DOCS_TYPES_SELECTED_SET
        }),

        getDocTypeModel(type) {
            return this.documentTypes.find(dt => dt.type === type)
        },

        getSystemDt(section) {
            return this.documentTypes.find(dt => dt.type === section) || {}
        },

        checkIsVisibleSystemDocType(section) {
            const dt = this.getSystemDt(section)
            return !!dt.isSystem && !!dt.isTopLevel && !dt.isHidden && this.checkIsVisibleDocTypeForCurrentUser(this.getSystemDt(section))
        },

        checkIsActiveDocTypeLink(uuid) {
            return _.get(this.dt, 'uuid') === uuid && this.$route.name === docSystemTypesDefaultModels.documents.route
        },

        async setCustomDocRoute(dt, openAsSeparateRoute) {
            await this.setDocTypeSelected(dt)
            await this.$router.push({name: 'documents', params: {dtUuid: openAsSeparateRoute ? dt.uuid : null}})
        },

        handleResize() { // Handle header collapse into "more" btn
            if (this.level === 2 || this.isResizeProcessing) {
                return
            }
            this.isResizeProcessing = true
            const container = document.getElementById('desktop-routes')
            const routes = document.querySelectorAll('#desktop-routes > a:not(#desktop-routes-more)')
            const moreLinks = document.querySelectorAll('#desktop-routes-more > a')

            for (let route of routes) { // clear
                route.classList.remove('d-none')
            }

            let width = 0
            let isAllVisible = true
            for (let route of routes) {
                width += route.clientWidth
                if (container.clientWidth > width) { // fit
                    for (let node of moreLinks) {
                        if (route.dataset.link && node.dataset.link && node.dataset.link === route.dataset.link) {
                            node.classList.add('d-none')
                        }
                    }
                    route.classList.remove('d-none')
                } else { // doesn't fit
                    route.classList.add('d-none')
                    for (let node of moreLinks) {
                        if (route.dataset.link && node.dataset.link && node.dataset.link === route.dataset.link) {
                            node.classList.remove('d-none')
                        }
                    }
                    if (isAllVisible) {
                        isAllVisible = false
                    }
                }
            }
            this.isAllRoutesVisible = isAllVisible
            this.isResizeProcessing = false
        }
    },
    mounted() {
        const routes = document.getElementById('desktop-routes')
        useResizeObserver(routes, this.handleResize)
    },
    updated() {
        this.handleResize() // NOTE: Resize observer does not catch dynamically added elements
    }
}
</script>

<style scoped lang="scss">
#desktop-routes {
    a {
        display: inline-block;
        padding: 8px 16px;

        &:hover {
            text-decoration: none;
        }
    }
}

.router-link-active {
    font-weight: bold;
}

.routes-wrap {
    max-width: calc(100vw - 320px);
    white-space: nowrap;
}

.inbox-unread-count-badge {
    position: absolute;
    color: white;
    font-weight: 600;
    font-size: 10px;
    padding: 1px 4px;
    top: 0;
    right: 0;
    border-radius: 50%;
    background: $color-purple-light;
}

#desktop-routes-more {
    a {
        display: block;
    }
}

.mobile-routes {
    a {
        padding: 12px;
        display: block;
        font-size: 18px;
    }
}

#more-links {
    display: inline-block;
    color: $color-purple-light;
    padding: 8px 0;
    margin-left: 16px;

    &:hover {
        cursor: pointer;
    }
}
</style>
